import React, { useState, useEffect } from "react";
import "./styles/base.css";

export default function SponsorshipApplication() {
  const STORAGE_KEY = "sponsorshipApp_v1";

  // Basic info
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Sponsorship questions (matching PolicyForm)
  const [sponsorshipReason, setSponsorshipReason] = useState("");
  const [scholarshipType, setScholarshipType] = useState(""); // "full" | "partial"
  const [partialPayAmount, setPartialPayAmount] = useState("");
  const [fundraisingEfforts, setFundraisingEfforts] = useState(""); // "yes" | "no"
  const [fundraisingDetails, setFundraisingDetails] = useState("");

  // Honeypot
  const [website, setWebsite] = useState("");

  // UI state
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        setFirstName(data.firstName || "");
        setLastName(data.lastName || "");
        setEmail(data.email || "");
        setPhone(data.phone || "");
        setSponsorshipReason(data.sponsorshipReason || "");
        setScholarshipType(data.scholarshipType || "");
        setPartialPayAmount(data.partialPayAmount || "");
        setFundraisingEfforts(data.fundraisingEfforts || "");
        setFundraisingDetails(data.fundraisingDetails || "");
      }
    } catch (e) {
      console.warn("Failed to load sponsorship app draft:", e);
    }
  }, []);

  // Autosave
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          firstName,
          lastName,
          email,
          phone,
          sponsorshipReason,
          scholarshipType,
          partialPayAmount,
          fundraisingEfforts,
          fundraisingDetails
        })
      );
    } catch (e) {
      // ignore
    }
  }, [firstName, lastName, email, phone, sponsorshipReason, scholarshipType, partialPayAmount, fundraisingEfforts, fundraisingDetails]);

  function validate() {
    const err = {};

    if (!firstName || !firstName.trim()) err.firstName = "First name is required.";
    if (!lastName || !lastName.trim()) err.lastName = "Last name is required.";
    if (!email || !email.trim()) err.email = "Email is required.";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) err.email = "Please enter a valid email.";
    if (!phone || !phone.trim()) err.phone = "Phone number is required.";

    if (!sponsorshipReason || !sponsorshipReason.trim()) err.sponsorshipReason = "Please explain why you require sponsorship.";
    if (!scholarshipType) err.scholarshipType = "Please select full or partial scholarship.";
    if (scholarshipType === "partial" && !partialPayAmount) err.partialPayAmount = "Please enter how much you can pay.";
    if (!fundraisingEfforts) err.fundraisingEfforts = "Please indicate if you've fundraised or spoken with a local church.";

    setErrors(err);
    return Object.keys(err).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // Honeypot check
    if (String(website || "").trim()) {
      return; // fail silently
    }

    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        sponsorshipReason: sponsorshipReason.trim(),
        scholarshipType,
        partialPayAmount: scholarshipType === "partial" ? String(partialPayAmount || "").trim() : "",
        fundraisingEfforts,
        fundraisingDetails: fundraisingDetails || ""
      };

      const res = await fetch("/.netlify/functions/submit-sponsorship", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) {
        alert(`Submission failed: ${data.error || "Unknown error"}`);
        return;
      }

      // Clear localStorage on success
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // ignore
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Sponsorship submission error:", err);
      alert("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="form-container" style={{ maxWidth: 600, margin: "2rem auto", padding: "2rem", textAlign: "center" }}>
        <h1>Thank You!</h1>
        <p style={{ fontSize: 16, lineHeight: 1.6, marginTop: 12 }}>
          Your sponsorship application has been received. We will review your request and reach out to you shortly at the email and phone number you provided.
        </p>
        <p style={{ fontSize: 14, color: "#666", marginTop: 12 }}>
          If you have any questions in the meantime, please contact our registrar.
        </p>
      </div>
    );
  }

  return (
    <form className="form-container" onSubmit={handleSubmit} autoComplete="on" style={{ maxWidth: 700, margin: "0 auto", padding: "2rem 1rem" }}>
      {/* Honeypot */}
      <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
        <label>
          Website
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
          />
        </label>
      </div>

      <header style={{ marginBottom: 32 }}>
        <h1 className="page-title">Sponsorship Application</h1>
        <p className="muted" style={{ fontSize: 16, lineHeight: 1.6 }}>
          If you need financial assistance to attend Camp Fusion, please complete this form. We will review your request and contact you to discuss available sponsorship options.
        </p>
      </header>

      {/* Contact Information */}
      <fieldset style={{ border: "none", padding: 0, marginBottom: 24 }}>
        <legend style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Contact Information</legend>

        <div className="field">
          <label className="field-label" htmlFor="firstName">First Name <span className="required">*</span></label>
          <input
            id="firstName"
            type="text"
            className="input"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setErrors((prev) => ({ ...prev, firstName: undefined }));
            }}
          />
          {errors.firstName && <div className="field-error">{errors.firstName}</div>}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="lastName">Last Name <span className="required">*</span></label>
          <input
            id="lastName"
            type="text"
            className="input"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setErrors((prev) => ({ ...prev, lastName: undefined }));
            }}
          />
          {errors.lastName && <div className="field-error">{errors.lastName}</div>}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="email">Email <span className="required">*</span></label>
          <input
            id="email"
            type="email"
            className="input"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
          />
          {errors.email && <div className="field-error">{errors.email}</div>}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="phone">Phone Number <span className="required">*</span></label>
          <input
            id="phone"
            type="tel"
            className="input"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setErrors((prev) => ({ ...prev, phone: undefined }));
            }}
          />
          {errors.phone && <div className="field-error">{errors.phone}</div>}
        </div>
      </fieldset>

      {/* Sponsorship Questions */}
      <fieldset style={{ border: "none", padding: 0, marginBottom: 24 }}>
        <legend style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Sponsorship Details</legend>

        <div className="field">
          <label className="field-label" htmlFor="sponsorshipReason">
            Why do you require sponsorship? <span className="required">*</span>
          </label>
          <textarea
            id="sponsorshipReason"
            className="input"
            value={sponsorshipReason}
            onChange={(e) => {
              setSponsorshipReason(e.target.value);
              setErrors((prev) => ({ ...prev, sponsorshipReason: undefined }));
            }}
            placeholder="e.g., I am a student, single parent, or facing unexpected hardship"
            rows={4}
          />
          {errors.sponsorshipReason && <div className="field-error">{errors.sponsorshipReason}</div>}
        </div>

        <div className="field">
          <label className="field-label">
            Do you need a full or partial scholarship? <span className="required">*</span>
          </label>
          <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="radio"
                name="scholarshipType"
                value="full"
                checked={scholarshipType === "full"}
                onChange={(e) => {
                  setScholarshipType(e.target.value);
                  setPartialPayAmount("");
                  setErrors((prev) => ({ ...prev, scholarshipType: undefined, partialPayAmount: undefined }));
                }}
              />
              Full Scholarship
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="radio"
                name="scholarshipType"
                value="partial"
                checked={scholarshipType === "partial"}
                onChange={(e) => {
                  setScholarshipType(e.target.value);
                  setErrors((prev) => ({ ...prev, scholarshipType: undefined }));
                }}
              />
              Partial Scholarship
            </label>
          </div>
          {errors.scholarshipType && <div className="field-error">{errors.scholarshipType}</div>}
        </div>

        {scholarshipType === "partial" && (
          <div className="field">
            <label className="field-label" htmlFor="partialPayAmount">
              How much can you pay? <span className="required">*</span>
            </label>
            <input
              id="partialPayAmount"
              type="text"
              className="input"
              value={partialPayAmount}
              onChange={(e) => {
                setPartialPayAmount(e.target.value);
                setErrors((prev) => ({ ...prev, partialPayAmount: undefined }));
              }}
              placeholder="e.g., 50, 100, 200"
            />
            {errors.partialPayAmount && <div className="field-error">{errors.partialPayAmount}</div>}
          </div>
        )}

        <div className="field">
          <label className="field-label">
            Have you done anything to help fundraise or spoken with a local church about sponsorship? <span className="required">*</span>
          </label>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="radio"
                name="fundraisingEfforts"
                value="yes"
                checked={fundraisingEfforts === "yes"}
                onChange={(e) => {
                  setFundraisingEfforts(e.target.value);
                  setErrors((prev) => ({ ...prev, fundraisingEfforts: undefined }));
                }}
              />
              Yes
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="radio"
                name="fundraisingEfforts"
                value="no"
                checked={fundraisingEfforts === "no"}
                onChange={(e) => {
                  setFundraisingEfforts(e.target.value);
                  setErrors((prev) => ({ ...prev, fundraisingEfforts: undefined }));
                }}
              />
              No
            </label>
          </div>
          {errors.fundraisingEfforts && <div className="field-error">{errors.fundraisingEfforts}</div>}
        </div>

        {fundraisingEfforts === "yes" && (
          <div className="field">
            <label className="field-label" htmlFor="fundraisingDetails">
              Please tell us what you've done (optional)
            </label>
            <textarea
              id="fundraisingDetails"
              className="input"
              value={fundraisingDetails}
              onChange={(e) => setFundraisingDetails(e.target.value)}
              placeholder="e.g., held a bake sale, spoke with St. John's church about assistance"
              rows={3}
            />
          </div>
        )}
      </fieldset>

      <div className="form-footer">
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? "Submitting…" : "Submit Application"}
        </button>
      </div>
    </form>
  );
}
